import { useState } from 'react';
import { useMeasure } from 'react-use';
import { FixedSizeGrid as Grid } from 'react-window';
import Empty from '../Empty/Empty';
import { GalleryItem } from './VirtualGallery.components';
import { useDimensions } from './VirtualGallery.hooks';
import { Item, Wrapper } from './VirtualGallery.style';

export type VirtualGalleryProps = {
  items: any[]; // Array of items with a defined structure
  baseImageUrl: string;
  onClick: (item: any) => void;
  onAction: OnAction;
  isLoading: boolean;
  small: boolean;
  itemActions?: IRowAction[];
  children?: React.ReactNode;
  itemSize?: { width: number; height: number };
  hideId?: boolean;
  fixedComponent?: React.ReactNode;
};

export function VirtualGallery(props: VirtualGalleryProps) {
  const { items, isLoading, small, itemSize } = props;
  const [ref, { width, height }] = useMeasure<HTMLDivElement>();
  const { columnCount, columnWidth, rowHeight } = useDimensions(width, itemSize, small);
  const rowCount = Math.ceil(items.length / columnCount);
  const [hoverId, setHoverId] = useState<string>('');

  const Cell = (itemProps: any) => {
    const itemIndex = itemProps.rowIndex * columnCount + itemProps.columnIndex;
    const item = items[itemIndex];
    let Cmp = props.children;

    if (!item) return <Item style={itemProps.style} />;

    if (item.id === 'fixed' && props.fixedComponent) {
      Cmp = props.fixedComponent;
    }

    return (
      <GalleryItem
        key={item.id}
        {...itemProps}
        {...props}
        item={item}
        isHovered={hoverId === item.id}
        onHover={setHoverId}
      >
        {Cmp}
      </GalleryItem>
    );
  };

  if (isLoading) {
    return <Empty>Loading</Empty>;
  }

  if (items.length === 0) {
    return <Empty>No generators</Empty>;
  }

  function renderInner() {
    // or height is infinity
    if (!height || !width || rowCount === Infinity || isNaN(columnWidth)) {
      return <></>;
    }

    return (
      <Grid
        columnCount={columnCount}
        rowCount={rowCount}
        columnWidth={columnWidth}
        rowHeight={rowHeight}
        width={width - 4}
        height={height - 10}
        itemData={items}
      >
        {Cell}
      </Grid>
    );
  }

  return (
    <Wrapper className='VirtualGallery-wrapper' data-testid='VirtualGallery-wrapper' ref={ref}>
      {renderInner()}
    </Wrapper>
  );
}

export default VirtualGallery;
